// eslint-disable-next-line simple-import-sort/imports
import Leaflet from 'leaflet';
import 'leaflet.pm';
import 'leaflet/dist/leaflet.css';
import 'leaflet.pm/dist/leaflet.pm.css';
import 'app/libs/leafletRuler';

import { Stack } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import {
  setBounds,
  setGeofenceChangedItem,
  setGeofenceMode,
  setGeofenceUpdateItem,
  setTransportConditionSelectedItem,
  setTransportSelectedItem,
  setHistorySelectedTime,
} from 'app/actions/map';
import { Protect, RotatedMarker } from 'app/components';
import { GeofenceMarkerIcon, TransportConditionParkingIcon, TransportMarkerIcon, TransportMarkerSquareIcon } from 'app/icons';
import {
  MapLeafletCanvasMarkersLayer,
  MapLeafletControls,
  MapLeafletGeofenceDialog,
  MapLeafletGeofencePopup,
  MapLeafletGeofenceShapeChangeDialog,
  MapLeafletGeofenceTooltip,
  MapLeafletMultiColorsPolyline,
  MapLeafletTransportParkingTooltip,
  MapLeafletTransportTooltip,
} from 'app/pages/Map';
import { useInterval } from 'app/services';
import { geofenceOptionsUpdate } from 'app/store/map';
import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import ReactDOMServer from 'react-dom/server';
import { Circle, LayerGroup, LayersControl, Map, Marker, Polygon, TileLayer, Tooltip } from 'react-leaflet';
import { useDispatch, useSelector } from 'react-redux';
import sphereKnn from 'sphere-knn';
import HistoryTimeLime from 'app/pages/Map/History/HistoryTimeLime';
import moment from 'moment';
import { setTransportItem } from 'app/actions/issue';

const MapLeaflet = forwardRef((props, ref) => {
  const mapRef = useRef();
  const theme = useTheme();
  const dispatch = useDispatch();
  const {
    zoom,
    bounds,
    layerSelected,
    geofenceMode,
    geofenceUpdatedMode,
    filterTab,
    geofenceItems,
    transportItems,
    transportWindowMinimize,
    transportGeofenceView,
    transportSelectedItem,
    transportConditionSelectedItem,
    geofenceCheckedId,
    geofenceUpdatedItem,
    geofenceChangedItem,
    isHistoryOpen,
    historySelectedTime,
    transportHistoryTracksItems,
    historyTracksVisible,
  } = useSelector((state) => state.map);

  const transportCheckedItems = useSelector((state) =>
    state.map.transportFilterCheckedType === 'filter' ? state.map.transportProcessingCheckedItems : state.map.transportCheckedItems
  );
  const geofenceModeRef = useRef(geofenceMode);
  const filterTabRef = useRef(filterTab);
  const historyBoundsFittedKeyRef = useRef(null);
  const [datetimeInterval, setDatetimeInterval] = useState(moment());

  const geofenceVisible = useMemo(() => {
    return filterTab === 'geofence' || (filterTab === 'transport' && transportGeofenceView);
  }, [filterTab, transportGeofenceView]);

  const geofenceContainsItems = useMemo(() => {
    if (geofenceCheckedId.length) {
      return geofenceItems.filter(
        (geofence) => geofenceCheckedId.includes(geofence.id) && bounds.contains(new Leaflet.latLng(geofence.latitude, geofence.longitude))
      );
    }

    return geofenceItems.filter((geofence) => bounds.contains(new Leaflet.latLng(geofence.latitude, geofence.longitude)));
  }, [zoom, bounds, geofenceItems, geofenceCheckedId]);

  // const transportCheckedItems = useMemo(() => {
  //     switch (transportFilterCheckedType) {
  //         case 'filter':
  //             return transportProcessingCheckedItems
  //         case 'all':
  //             return transportCheckedItemsAll
  //     }
  // }, [transportCheckedItemsAll, transportProcessingCheckedItems, transportFilterCheckedType]);

  const transportContainsItems = useMemo(() => {
    const filterItems = transportItems.filter((transportItem) => transportCheckedItems.includes(transportItem.id));

    return filterItems.reduce((accumulator, currentValue) => {
      const conditions =
        config.transportDeviceTypes[currentValue.device_type] === 'galileosky'
          ? currentValue.conditions.filter((condition) => condition.data.geodata !== 'ERR')
          : [];

      if (conditions.length) {
        accumulator.push({ ...currentValue, conditions });
      }

      return accumulator;
    }, []);
  }, [zoom, bounds, transportItems, transportCheckedItems]);

  const transportTrackItems = useMemo(() => {
    if (!transportSelectedItem || !transportConditionSelectedItem || isHistoryOpen) {
      return [];
    }

    return transportSelectedItem.conditions.filter(
      (condition) =>
        condition.type === 'message' &&
        condition.data.geodata !== 'ERR' &&
        moment(condition.datetime).isSameOrBefore(moment(transportConditionSelectedItem.datetime)) &&
        moment(condition.datetime).isSameOrAfter(
          moment(transportConditionSelectedItem.datetime).subtract(transportSelectedItem.conditionOptions.interval, 'hours')
        )
    );
  }, [transportSelectedItem, transportConditionSelectedItem, isHistoryOpen]);
  const historyTransportTrackItems = Object.values(transportHistoryTracksItems);
  const historyTransportPositionItems = useMemo(() => {
    if (!isHistoryOpen || !historyTransportTrackItems.length) {
      return [];
    }
    return transportCheckedItems.reduce((acc, val) => {
      if (!transportHistoryTracksItems[val]) {
        return acc;
      }

      let last = transportHistoryTracksItems[val].reduceRight((acc, val) => {
        if (!acc.datetime) {
          if (moment(val.datetime).isSameOrBefore(moment(historySelectedTime))) {
            acc = val;
          }
        }
        return acc;
      }, {});
      // Если не найдена точка до выбранного времени, берем первую доступную
      if (!last.datetime && transportHistoryTracksItems[val].length > 0) {
        last = transportHistoryTracksItems[val][0];
      }
      if (last.datetime) {
        const transportItem = transportItems.find((item) => item.id === val);
        if (transportItem) {
          acc.push({
            ...last,
            name: transportItem.name || '',
            color: transportItem.color || '#000000',
          });
        }
      }
      return acc;
    }, []);
  }, [transportCheckedItems, transportHistoryTracksItems, transportItems, isHistoryOpen, historySelectedTime]);
  const transportParkingItems = useMemo(() => {
    if (transportSelectedItem && config.transportDeviceTypes[transportSelectedItem.device_type] === 'galileosky') {
      return transportTrackItems.slice(1).reduce((accumulator, currentValue, index, array) => {
        const previousCondition = index ? array[index - 1] : transportTrackItems[0];
        const parkingStartEvent = currentValue.messages.some((message) => message.key === 'parking:start');
        const parkingEndEvent = currentValue.messages.some((message) => message.key === 'parking:off');

        if (parkingStartEvent && currentValue.data.is_parked === 1 && previousCondition.data.is_parked === 0) {
          accumulator.push({
            name: transportSelectedItem.name,
            parking_time_start: currentValue.datetime,
            lat: currentValue.data.lat,
            lon: currentValue.data.lon,
          });
        }

        if (parkingEndEvent && currentValue.data.is_parked === 0 && previousCondition.data.is_parked === 1 && accumulator.length !== 0) {
          accumulator[accumulator.length - 1].interval = moment
            .utc(moment(currentValue.datetime).diff(moment(accumulator[accumulator.length - 1].parking_time_start)))
            .format('HH:mm:ss');
        }

        return accumulator;
      }, []);
    }

    return [];
  }, [transportSelectedItem, transportConditionSelectedItem, transportTrackItems]);

  const transportConditionSelectedValidItem = useMemo(() => {
    if (
      transportSelectedItem &&
      transportConditionSelectedItem &&
      config.transportDeviceTypes[transportSelectedItem.device_type] === 'galileosky'
    ) {
      if (transportConditionSelectedItem.data?.geodata !== 'ERR') {
        return transportConditionSelectedItem;
      }

      const currentIndex = transportSelectedItem.conditions.findIndex(
        (condition) => condition.datetime === transportConditionSelectedItem.datetime && condition.type === 'message'
      );

      if (currentIndex !== -1) {
        for (let i = currentIndex - 1; i >= 0; i--) {
          if (!transportSelectedItem.conditions[i]) {
            return transportConditionSelectedItem;
          }

          if (transportSelectedItem.conditions[i].data.geodata !== 'ERR' && transportSelectedItem.conditions[i].type === 'message') {
            return transportSelectedItem.conditions[i];
          }
        }
      }

      return transportConditionSelectedItem;
    }

    return null;
  }, [transportSelectedItem, transportConditionSelectedItem]);

  const handleMapLayerChange = (event) => {
    const center = mapRef.current.leafletElement.getCenter();
    const zoom = mapRef.current.leafletElement.getZoom();

    mapRef.current.leafletElement.options.crs = event.name.includes('Yandex') ? Leaflet.CRS.EPSG3395 : Leaflet.CRS.EPSG3857;
    mapRef.current.leafletElement._resetView(center, zoom, true);
  };

  const handleGeofenceBounding = () =>
    dispatch(setBounds(mapRef.current.leafletElement.getZoom(), mapRef.current.leafletElement.getBounds()));

  const handleMapPopupClose = () => {
    mapRef.current.leafletElement.closePopup();
  };

  const handlePolylineClick = (event) => {
    const lookup = sphereKnn(event.target._latlngs);
    const closestPoint = lookup(event.latlng.lat, event.latlng.lng, 1);

    if (closestPoint.length) {
      dispatch(
        setTransportConditionSelectedItem(
          transportSelectedItem.conditions.find(
            (condition) => condition.data.lat === closestPoint[0].lat && condition.data.lon === closestPoint[0].lng
          )
        )
      );
    }
  };
  const handleHistoryPolylineClick = (event, id) => {
    const lookup = sphereKnn(event.target._latlngs);
    const closestPoint = lookup(event.latlng.lat, event.latlng.lng, 1);
    if (closestPoint.length) {
      let index = transportHistoryTracksItems[id].findIndex(
        (condition) => condition.lat === closestPoint[0].lat && condition.lon === closestPoint[0].lng
      );
      const conditionSelectedItem = transportHistoryTracksItems[id][index];

      // Если окно ТС закрыто - открываем окно для кликнутого ТС
      if (!transportSelectedItem) {
        const transport = transportItems.find((item) => item.id === id);
        dispatch(setTransportSelectedItem(transport));
        dispatch(setTransportItem(transport));
        dispatch(setTransportConditionSelectedItem(conditionSelectedItem));
        // Обновляем historySelectedTime для синхронизации таймлайна
        if (conditionSelectedItem && conditionSelectedItem.datetime) {
          dispatch(setHistorySelectedTime(conditionSelectedItem.datetime));
        }
      } else if (transportSelectedItem.id === id) {
        // Если окно открыто для того же ТС - обновляем событие
        const selectedCondition = transportSelectedItem.conditions[index];
        dispatch(setTransportConditionSelectedItem(selectedCondition));
        // Обновляем historySelectedTime для синхронизации таймлайна
        if (selectedCondition && selectedCondition.datetime) {
          dispatch(setHistorySelectedTime(selectedCondition.datetime));
        }
      } else {
        // Если окно открыто для другого ТС - только обновляем время на таймлайне
        // и выбираем ближайшее событие у открытой ТС
        if (conditionSelectedItem && conditionSelectedItem.datetime) {
          const clickedTime = moment(conditionSelectedItem.datetime);
          dispatch(setHistorySelectedTime(conditionSelectedItem.datetime));

          // Находим ближайшее событие у открытой ТС по времени
          if (transportSelectedItem.conditions) {
            const conditions = transportSelectedItem.conditions.filter((condition) => condition.type === 'message');

            if (conditions.length > 0) {
              let closestCondition = conditions[0];
              let minDiff = Math.abs(moment(closestCondition.datetime).diff(clickedTime));

              for (let i = 1; i < conditions.length; i++) {
                const diff = Math.abs(moment(conditions[i].datetime).diff(clickedTime));
                if (diff < minDiff) {
                  minDiff = diff;
                  closestCondition = conditions[i];
                }
              }

              // Выбираем ближайшее событие у открытой ТС
              dispatch(setTransportConditionSelectedItem(closestCondition));
            }
          }
        }
      }
    }
  };

  const handleGeofenceDialogClose = () => {
    if (geofenceUpdatedMode === 'create') {
      mapRef.current.leafletElement.eachLayer((layer) => {
        if (layer._leaflet_id === geofenceUpdatedItem.leafletId) {
          mapRef.current.leafletElement.removeLayer(layer);
        }
      });
    }

    dispatch(setGeofenceMode(''));
    dispatch(setGeofenceUpdateItem(null));
  };

  const handleGeofenceShapeChangeDialogClose = () => dispatch(setGeofenceChangedItem(null));

  const handleGeofenceCreate = (event) => {
    const params = {
      leafletId: event.layer._leaflet_id,
    };

    switch (event.shape) {
      case 'Circle':
        params.shape = 'Circle';
        params.radius = Math.round(event.layer.options.radius);
        params.latitude = event.layer.getLatLng().lat;
        params.longitude = event.layer.getLatLng().lng;
        break;
      case 'Polygon':
        params.shape = 'Polygon';
        params.latitude = event.layer.getBounds().getCenter().lat;
        params.longitude = event.layer.getBounds().getCenter().lng;
        params.geometry = event.layer.getLatLngs()[0].map((coordinates) => Object.values(coordinates).reverse());
        break;
    }

    dispatch(setGeofenceUpdateItem(params));
  };

  const handleGeofenceUpdateStart = (event) => {
    event.target.closePopup();
    event.target.closeTooltip();
    event.target.setStyle({
      color: theme.palette.warning.dark,
      fillColor: theme.palette.warning.dark,
    });
  };

  const handleGeofenceUpdateEnd = (event) => {
    const params = {
      type: 'coordinates',
    };

    switch (Object.prototype.hasOwnProperty.call(event.target.options, 'radius')) {
      case true:
        params.shape = 'Circle';
        params.radius = Math.round(event.target.getRadius());
        params.latitude = event.target.getLatLng().lat;
        params.longitude = event.target.getLatLng().lng;
        break;
      case false:
        params.shape = 'Polygon';
        params.latitude = event.target.getBounds().getCenter().lat;
        params.longitude = event.target.getBounds().getCenter().lng;
        params.geometry = event.target.getLatLngs()[0].map((coordinates) => Object.values(coordinates).reverse());
        break;
    }

    dispatch(geofenceOptionsUpdate(event.target.options.id, params)).then((response) => {
      event.target.setStyle({
        color: response.color,
        fillColor: response.color,
      });
    });
  };

  useImperativeHandle(ref, () => ({
    getZoom: () => mapRef.current.leafletElement.getZoom(),
    getBounds: () => mapRef.current.leafletElement.getBounds(),
    handleClosePopup: () => mapRef.current.leafletElement.closePopup(),
    handleRulerMode: () => document.getElementsByClassName('leaflet-ruler')[0].click(),
    handleFlyTo: (coordinates, zoom = undefined) => {
      const targetZoom = zoom ?? mapRef.current.leafletElement.getZoom();
      const targetPoint = mapRef.current.leafletElement.project(new Leaflet.LatLng(coordinates[0], coordinates[1]), targetZoom);

      mapRef.current.leafletElement.setView(
        mapRef.current.leafletElement.unproject(
          transportSelectedItem && !transportWindowMinimize
            ? targetPoint.subtract([config.mapTransportViewOptions.offsetX, config.mapTransportViewOptions.offsetY])
            : targetPoint,
          targetZoom
        ),
        targetZoom
      );
    },
  }));

  useInterval(() => {
    if (moment().seconds() === 0) {
      setDatetimeInterval(moment());
    }
  }, 1000);

  useEffect(() => {
    filterTabRef.current = filterTab;
  }, [filterTab]);

  // Автоматическое масштабирование карты при включении режима "машина времени"
  useEffect(() => {
    if (!isHistoryOpen) {
      // Сбрасываем ключ при выключении режима
      historyBoundsFittedKeyRef.current = null;
      return;
    }

    if (
      isHistoryOpen &&
      mapRef.current &&
      transportHistoryTracksItems &&
      Object.keys(transportHistoryTracksItems).length > 0 &&
      transportCheckedItems.length > 0
    ) {
      // Проверяем, что для всех выбранных ТС есть треки (или хотя бы для одного)
      const hasTracksForSelected = transportCheckedItems.some(
        (transportId) =>
          transportHistoryTracksItems[transportId] &&
          Array.isArray(transportHistoryTracksItems[transportId]) &&
          transportHistoryTracksItems[transportId].length > 0
      );

      if (!hasTracksForSelected) {
        // Треки еще не загружены, ждем
        return;
      }

      // Создаем ключ на основе списка выбранных ТС и наличия треков
      const tracksKey = transportCheckedItems
        .map((id) => {
          const track = transportHistoryTracksItems[id];
          return track && Array.isArray(track) ? track.length : 0;
        })
        .join(',');
      const currentKey = `${transportCheckedItems.slice().sort().join(',')}_${tracksKey}`;

      // Масштабируем только если изменился набор выбранных ТС или загрузились треки
      if (historyBoundsFittedKeyRef.current !== currentKey) {
        historyBoundsFittedKeyRef.current = currentKey;

        // Собираем все точки всех треков и текущие позиции ТС
        const allPoints = [];

        // Добавляем точки треков
        transportCheckedItems.forEach((transportId) => {
          const track = transportHistoryTracksItems[transportId];
          if (Array.isArray(track) && track.length > 0) {
            track.forEach((point) => {
              if (point.lat && point.lon) {
                allPoints.push([point.lat, point.lon]);
              }
            });
          }
        });

        // Если есть точки, масштабируем карту с максимальным зумом
        if (allPoints.length > 0) {
          setTimeout(() => {
            const bounds = Leaflet.latLngBounds(allPoints);
            const map = mapRef.current.leafletElement;

            // Используем fitBounds с минимальными отступами для максимального масштабирования
            // maxZoom не ограничиваем, чтобы получить максимально возможный зум
            map.fitBounds(bounds, {
              padding: [20, 20], // Минимальные отступы для максимального масштабирования
            });
          }, 150); // Увеличена задержка для корректного рендеринга
        }
      }
    }
  }, [isHistoryOpen, transportHistoryTracksItems, transportCheckedItems]);

  useEffect(() => {
    if (geofenceMode.length !== 0) {
      geofenceModeRef.current = geofenceMode;
    }

    switch (geofenceModeRef.current) {
      case 'Poly':
        if (!geofenceMode) {
          mapRef.current.leafletElement.pm.disableDraw('Poly');
          break;
        }

        mapRef.current.leafletElement.pm.enableDraw('Poly', config.mapDrawOptions);
        break;
      case 'Circle':
        if (!geofenceMode) {
          mapRef.current.leafletElement.pm.disableDraw('Circle');
          break;
        }

        mapRef.current.leafletElement.pm.enableDraw('Circle', config.mapDrawOptions);
        break;
      case 'Edit':
        mapRef.current.leafletElement.pm.toggleGlobalEditMode();
        mapRef.current.leafletElement.eachLayer((layer) =>
          ['pm:markerdragstart', 'pm:markerdragend', 'pm:vertexadded', 'pm:vertexremoved'].map((event) => {
            if (Object.prototype.hasOwnProperty.call(layer.options, 'id')) {
              mapRef.current.leafletElement.pm.globalEditEnabled()
                ? event === 'pm:markerdragstart'
                  ? layer.addEventListener(event, (event) => handleGeofenceUpdateStart(event))
                  : layer.addEventListener(event, (event) => handleGeofenceUpdateEnd(event))
                : event === 'pm:markerdragstart'
                  ? layer.removeEventListener(event, (event) => handleGeofenceUpdateStart(event))
                  : layer.removeEventListener(event, (event) => handleGeofenceUpdateEnd(event));
            }
          })
        );
        break;
      case 'Drag':
        mapRef.current.leafletElement.pm.globalEditEnabled() && mapRef.current.leafletElement.pm.disableGlobalEditMode();
        mapRef.current.leafletElement.pm.toggleGlobalDragMode();
        mapRef.current.leafletElement.eachLayer((layer) =>
          ['pm:dragstart', 'pm:dragend'].map((event) => {
            if (Object.prototype.hasOwnProperty.call(layer.options, 'id')) {
              mapRef.current.leafletElement.pm.globalDragModeEnabled()
                ? event === 'pm:dragstart'
                  ? layer.addEventListener(event, (event) => handleGeofenceUpdateStart(event))
                  : layer.addEventListener(event, (event) => handleGeofenceUpdateEnd(event))
                : event === 'pm:dragstart'
                  ? layer.removeEventListener(event, (event) => handleGeofenceUpdateStart(event))
                  : layer.removeEventListener(event, (event) => handleGeofenceUpdateEnd(event));
            }
          })
        );
        break;
    }
  }, [geofenceMode]);

  useEffect(() => {
    if (geofenceVisible && zoom >= config.mapMinGeofenceMarkerZoom) {
      mapRef.current.leafletElement.eachLayer((layer) => {
        const tooltip = layer.getTooltip();

        if (tooltip) {
          layer.unbindTooltip().bindTooltip(tooltip, {
            permanent: true,
          });
        }
      });
    }
  }, [geofenceContainsItems]);

  useEffect(() => {
    mapRef.current.leafletElement.flyToBounds(mapRef.current.leafletElement.getBounds());
  }, [filterTab, geofenceCheckedId]);

  useEffect(() => {
    Leaflet.control.ruler().addTo(mapRef.current.leafletElement);
  }, []);

  useEffect(() => {
    let autoPanStart = false;

    mapRef.current.leafletElement.pm.setLang('ru');
    mapRef.current.leafletElement.addEventListener('pm:create', handleGeofenceCreate);
    mapRef.current.leafletElement.addEventListener('autopanstart', () => {
      autoPanStart = true;
    });
    mapRef.current.leafletElement.addEventListener('moveend', () => {
      if (!autoPanStart) {
        handleGeofenceBounding();
      }

      autoPanStart = false;
    });
  }, []);
  window.moment = moment;
  return (
    <React.Fragment>
      <Stack
        direction='column'
        alignItems='center'
        justifyContent='center'
        sx={(theme) => ({
          height: '100%',
          flex: '1 1 auto',
          '.leaflet-container': {
            zIndex: '1000',
            width: '100%',
            height: '100%',
          },
          '.leaflet-control-layers': {
            display: 'none',
          },
          '.leaflet-control-attribution svg': {
            display: 'none!important',
          },
          '.leaflet-tooltip-bottom': {
            fontSize: '12px',
            fontFamily: '"Roboto",sans-serif',
            borderRadius: theme.shape.borderRadius,
            padding: '3px 8px',

            // color: theme.palette.common.white,
            backgroundColor: theme.palette.grey.dark,
            border: 'none',
            '&:before': {
              borderBottomColor: 'transparent',
            },
          },
          '.leaflet-tooltip-top': {
            '&:before': {
              borderTopColor: 'transparent',
            },
          },
          '.leaflet-popup.leaflet-popup-transport, .leaflet-popup.leaflet-popup-geofence': {
            border: '0',
            opacity: '1',
            padding: theme.spacing(0),
            transition: 'none',
            '.leaflet-popup-content-wrapper': {
              padding: theme.spacing(0),
              boxShadow: '0 1px 3px ' + alpha(theme.palette.grey.main, 0.12),
            },
            '.leaflet-popup-content, .leaflet-popup-content p': {
              margin: theme.spacing(0),
            },
            '.leaflet-popup-tip': {
              display: 'none',
            },
            '&:not(:empty):before': {
              bottom: '-10px',
              width: '10px',
              left: '50%',
              height: '10px',
              zIndex: '0',
              content: '""',
              display: 'block',
              position: 'absolute',
              transform: 'translateX(-50%) translateY(-50%) rotate(45deg)',
              boxShadow: '0 1px 3px -2px ' + alpha(theme.palette.grey.main, 0.12),
              bgcolor: 'background.paper',
            },
          },
          '.leaflet-marker-icon.leaflet-marker-icon-transport': {
            border: '0',
            bgcolor: 'transparent',
            svg: {
              width: '100%',
              height: '100%',
            },
          },
          '.leaflet-tooltip.leaflet-tooltip-bottom-transport': {
            whiteSpace: 'nowrap',
            padding: '0',
            marginTop: '16px',
            borderRadius: theme.shape.borderRadius,
            bgcolor: 'background.paper',
            border: 'none',
            pointerEvents: 'auto',
            cursor: 'pointer',
            boxShadow: '0 1px 3px ' + alpha(theme.palette.grey.main, 0.12),
            '&:not(:empty):before': {
              bottom: '-10px',
              width: '10px',
              left: '50%',
              height: '10px',
              margin: '0',
              border: '0',
              zIndex: '0',
              content: '""',
              display: 'block',
              position: 'absolute',
              transform: 'translateX(-50%) translateY(-50%) rotate(45deg)',
              boxShadow: '0px -2px 3px -2px ' + alpha(theme.palette.grey.main, 0.12),
              bgcolor: 'background.paper',
            },
          },
          '.leaflet-tooltip.leaflet-tooltip-top-geofence': {
            padding: '0',
            marginBottom: '15px',
            opacity: '1!important',
            marginTop: '-9px !important',
            borderRadius: theme.shape.borderRadius,
            bgcolor: 'background.paper',
            border: 'none',
            boxShadow: '0 1px 3px ' + alpha(theme.palette.grey.main, 0.12),
            ...(zoom < config.mapMinGeofenceMarkerZoom && {
              marginTop: '-17px !important',
              marginLeft: '-2px !important',
            }),
            '&:not(:empty):before': {
              bottom: '-10px',
              width: '10px',
              left: '50%',
              height: '10px',
              margin: '0',
              border: '0',
              zIndex: '0',
              content: '""',
              display: 'block',
              position: 'absolute',
              transform: 'translateX(-50%) translateY(-50%) rotate(45deg)',
              boxShadow: '0px 1px 3px -2px ' + alpha(theme.palette.grey.main, 0.12),
              bgcolor: 'background.paper',
              ...(zoom < config.mapMinGeofenceMarkerZoom && {
                width: '8px',
                height: '8px',
                bottom: '-8px',
              }),
            },
          },
          '.leaflet-tooltip.leaflet-tooltip-top-transport-parking': {
            whiteSpace: 'nowrap',
            padding: '0',
            marginTop: '-20px',
            marginLeft: '-3px',
            opacity: '1!important',
            borderRadius: theme.shape.borderRadius,
            bgcolor: 'background.paper',
            border: 'none',
            boxShadow: '0 1px 3px ' + alpha(theme.palette.grey.main, 0.12),
          },
          '.leaflet-ruler': {
            visibility: 'hidden',
          },
          '.leaflet-ruler-bar': {
            backgroundColor: '#FFFFFF',
          },
          '.leaflet-ruler-result-tooltip': {
            backgroundColor: '#FFFFFF',
            borderWidth: 'medium',
            borderRadius: '4px',
            fontSize: 'smaller',
          },
          '.leaflet-ruler-moving-tooltip': {
            backgroundColor: 'rgba(255, 255, 255, 1)',
            backgroundClip: 'padding-box',
            borderRadius: '4px',
            fontSize: 'smaller',
          },
          '.leaflet-ruler-plus-length': {
            paddingLeft: '45px',
          },
          '.leaflet-canvas-interactive': {
            cursor: 'auto',
          },
        })}>
        <Map
          ref={mapRef}
          center={config.mapCenter}
          zoom={zoom}
          minZoom={3}
          maxZoom={18}
          crs={Leaflet.CRS.EPSG3395}
          scrollWheelZoom
          zoomControl={false}
          closePopupOnClick={false}
          onbaselayerchange={handleMapLayerChange}>
          <LayersControl>
            <LayersControl.BaseLayer name='Google' checked={layerSelected === 'Google'}>
              <TileLayer
                url='http://{s}.google.com/vt/lyrs=m&x={x}&y={y}&z={z}'
                subdomains={['mt0', 'mt1', 'mt2', 'mt3']}
                attribution='&amp;copy <a href="https://google.com/copyright" target="_blank">Google</a>'></TileLayer>
            </LayersControl.BaseLayer>
            <LayersControl.BaseLayer name='GoogleHybrid' checked={layerSelected === 'GoogleHybrid'}>
              <TileLayer
                url='https://{s}.google.com/vt/lyrs=s,h&x={x}&y={y}&z={z}'
                subdomains={['mt0', 'mt1', 'mt2', 'mt3']}
                attribution='&amp;copy <a href="https://google.com/copyright" target="_blank">Google</a>'></TileLayer>
            </LayersControl.BaseLayer>
            <LayersControl.BaseLayer name='Yandex' checked={layerSelected === 'Yandex'}>
              <TileLayer
                url='https://core-renderer-tiles.maps.yandex.net/tiles?l=map&x={x}&y={y}&z={z}&scale=1&lang=ru_R'
                attribution='&amp;copy <a href="https://yandex.ru/copyright" target="_blank">Yandex</a>'></TileLayer>
            </LayersControl.BaseLayer>
            <LayersControl.BaseLayer name='YandexHybrid' checked={layerSelected === 'YandexHybrid'}>
              <LayerGroup>
                <TileLayer
                  url='https://core-sat.maps.yandex.net/tiles?l=sat&x={x}&y={y}&z={z}&scale=1&lang=ru_RU'
                  attribution='&amp;copy <a href="https://yandex.ru/copyright" target="_blank">Yandex</a>'></TileLayer>
                <TileLayer
                  url='https://core-renderer-tiles.maps.yandex.net/tiles?l=skl&x={x}&y={y}&z={z}&scale=1&lang=ru_RU'
                  attribution='&amp;copy <a href="https://yandex.ru/copyright" target="_blank">Yandex</a>'></TileLayer>
              </LayerGroup>
            </LayersControl.BaseLayer>
            <LayersControl.BaseLayer name='OpenStreetMap' checked={layerSelected === 'OpenStreet'}>
              <TileLayer
                url='http://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
                attribution='&amp;copy <a href="http://osm.org/copyright">OpenStreetMap</a> contributors'></TileLayer>
            </LayersControl.BaseLayer>
            <LayersControl.BaseLayer name='OpenStreetMapSatellite' checked={layerSelected === 'OpenStreetSatellite'}>
              <TileLayer
                url='http://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'
                subdomains={['otile1', 'otile2', 'otile3', 'otile4']}
                attribution='&amp;copy <a href="http://osm.org/copyright">OpenStreetMap</a> contributors'></TileLayer>
            </LayersControl.BaseLayer>
          </LayersControl>
          {!isHistoryOpen && transportSelectedItem?.conditionOptions.visible && (
            <MapLeafletMultiColorsPolyline
              pmIgnore
              lineCap='butt'
              weight={7}
              options={config.mapMultiColorsPolylineOptions.colors}
              positions={transportTrackItems.map((condition) => [
                condition.data.lat,
                condition.data.lon,
                condition.data.can_speed ?? condition.data.speed,
              ])}
              onClick={handlePolylineClick}
              optionIdxFn={(point) => {
                const speedThresholds = [30, 80, 120];

                for (let i = 0; i < speedThresholds.length; ++i) {
                  if (point[2] <= speedThresholds[i]) {
                    return i;
                  }
                }

                return speedThresholds.length;
              }}
            />
          )}
          {!isHistoryOpen &&
            transportSelectedItem?.parkingPointsOptions.visible &&
            transportParkingItems.map((parkingPoint, key) => (
              <Marker
                key={key}
                icon={Leaflet.divIcon({
                  html: ReactDOMServer.renderToString(<TransportConditionParkingIcon />),
                  className: 'leaflet-marker-icon-geofence',
                  iconSize: 30,
                })}
                position={[parkingPoint.lat, parkingPoint.lon]}>
                <MapLeafletTransportParkingTooltip value={parkingPoint} />
              </Marker>
            ))}

          {!isHistoryOpen && (
            <MapLeafletCanvasMarkersLayer>
              {transportContainsItems.map((transportItem) => (
                <RotatedMarker
                  pmIgnore
                  key={transportItem.id}
                  minTooltipZoom={config.mapMinTransportTooltipZoom}
                  icon={Leaflet.divIcon({
                    html:
                      transportItem.conditions[transportItem.conditions.length - 1].data.can_speed === 0
                        ? ReactDOMServer.renderToString(<TransportMarkerSquareIcon fill={transportItem.color} strokeWidth={2} />)
                        : ReactDOMServer.renderToString(<TransportMarkerIcon fill={transportItem.color} strokeWidth={2} />),
                    className: 'leaflet-marker-icon-transport',
                    iconSize: [30, 30],
                    iconAnchor: [15, 15],
                  })}
                  rotationAngle={
                    transportItem.conditions[transportItem.conditions.length - 1].data.can_speed !== 0 &&
                    transportItem.conditions[transportItem.conditions.length - 1].data.course
                  }
                  position={[
                    transportItem.conditions[transportItem.conditions.length - 1].data.lat,
                    transportItem.conditions[transportItem.conditions.length - 1].data.lon,
                  ]}>
                  <Protect
                    permissions='read'
                    renderPermitted={() => (
                      <React.Fragment>
                        {/* <MapLeafletTransportPopup
                                                value={transportItem}
                                                onClose={handleMapPopupClose}
                                            /> */}
                        <MapLeafletTransportTooltip value={transportItem} datetime={datetimeInterval} permanent />
                      </React.Fragment>
                    )}
                    renderForbidden={() => <MapLeafletTransportTooltip value={transportItem} datetime={datetimeInterval} permanent />}
                  />
                </RotatedMarker>
              ))}
              {geofenceVisible &&
                zoom < config.mapMinGeofenceMarkerZoom &&
                geofenceContainsItems.map((geofence) => (
                  <Marker
                    id={geofence.id}
                    key={geofence.id}
                    minTooltipZoom={config.mapMinGeofenceTooltipZoom}
                    position={[geofence.latitude, geofence.longitude]}
                    icon={Leaflet.divIcon({
                      html: ReactDOMServer.renderToString(<GeofenceMarkerIcon fill={geofence.color} />),
                      className: 'leaflet-marker-icon-geofence',
                      iconSize: [30, 30],
                      iconAnchor: [15, 15],
                    })}>
                    <Protect
                      permissions={['create', 'delete', 'update']}
                      renderPermitted={() => (
                        <React.Fragment>
                          <MapLeafletGeofencePopup value={geofence} onClose={handleMapPopupClose} />
                          {geofence.type_id === 1 && geofence.short_name && <MapLeafletGeofenceTooltip value={geofence} permanent />}
                        </React.Fragment>
                      )}
                      renderForbidden={() => (
                        <React.Fragment>
                          {geofence.type_id === 1 && geofence.short_name && <MapLeafletGeofenceTooltip value={geofence} permanent />}
                        </React.Fragment>
                      )}
                    />
                  </Marker>
                ))}
              {transportConditionSelectedValidItem &&
                (transportCheckedItems.indexOf(transportSelectedItem.id) !== -1
                  ? transportConditionSelectedValidItem.datetime !==
                    transportSelectedItem?.conditions[transportSelectedItem.conditions.length - 1]?.datetime
                  : true) && (
                  <React.Fragment>
                    {!transportConditionSelectedItem?.hdopData && (
                      <RotatedMarker
                        pmIgnore
                        minTooltipZoom={config.mapMinTransportTooltipZoom}
                        icon={Leaflet.divIcon({
                          html:
                            transportConditionSelectedValidItem.data.can_speed === 0
                              ? ReactDOMServer.renderToString(
                                  <TransportMarkerSquareIcon fill={transportSelectedItem.color} strokeWidth={2} />
                                )
                              : ReactDOMServer.renderToString(<TransportMarkerIcon fill={transportSelectedItem.color} strokeWidth={2} />),
                          className: 'leaflet-marker-icon-transport',
                          iconSize: [30, 30],
                          iconAnchor: [15, 15],
                        })}
                        rotationAngle={
                          transportConditionSelectedValidItem.data.can_speed !== 0 && transportConditionSelectedValidItem.data.course
                        }
                        position={[transportConditionSelectedValidItem.data.lat, transportConditionSelectedValidItem.data.lon]}
                      />
                    )}
                    {transportConditionSelectedItem?.hdopData && (
                      <RotatedMarker
                        pmIgnore
                        minTooltipZoom={config.mapMinTransportTooltipZoom}
                        icon={Leaflet.divIcon({
                          html:
                            transportConditionSelectedItem.data.can_speed === 0
                              ? ReactDOMServer.renderToString(
                                  <TransportMarkerSquareIcon fill={transportSelectedItem.color} strokeWidth={2} />
                                )
                              : ReactDOMServer.renderToString(<TransportMarkerIcon fill={transportSelectedItem.color} strokeWidth={2} />),
                          className: 'leaflet-marker-icon-transport',
                          iconSize: [30, 30],
                          iconAnchor: [15, 15],
                        })}
                        rotationAngle={
                          transportConditionSelectedItem.hdopData.speed !== 0 && transportConditionSelectedItem.hdopData.course
                        }
                        position={[transportConditionSelectedItem.hdopData.lat, transportConditionSelectedItem.hdopData.lon]}
                      />
                    )}
                  </React.Fragment>
                )}
            </MapLeafletCanvasMarkersLayer>
          )}
          {geofenceVisible &&
            zoom >= config.mapMinGeofenceMarkerZoom &&
            geofenceContainsItems.map((geofence) => {
              switch (geofence.shape) {
                case 'Circle':
                  return (
                    <Circle
                      id={geofence.id}
                      key={geofence.id}
                      weight={2}
                      color={geofence.color}
                      fillColor={geofence.color}
                      radius={geofence.radius}
                      center={[geofence.latitude, geofence.longitude]}>
                      <MapLeafletGeofencePopup value={geofence} onClose={handleMapPopupClose} />
                      {geofence.type_id === 1 && geofence.short_name && (
                        <Circle pmIgnore color='transparent' fillColor='transparent' radius={1} center={geofence.tooltipPoint}>
                          <MapLeafletGeofenceTooltip value={geofence} permanent />
                        </Circle>
                      )}
                    </Circle>
                  );
                case 'Polygon':
                  return (
                    <Polygon
                      id={geofence.id}
                      key={geofence.id}
                      color={geofence.color}
                      fillColor={geofence.color}
                      weight={2}
                      positions={geofence.geometry}>
                      <MapLeafletGeofencePopup value={geofence} onClose={handleMapPopupClose} />
                    </Polygon>
                  );
              }
            })}

          {isHistoryOpen &&
            historyTracksVisible &&
            historyTransportTrackItems.map((transportHistoryTrackItems) => {
              return (
                <MapLeafletMultiColorsPolyline
                  pmIgnore
                  lineCap='butt'
                  weight={7}
                  options={config.mapMultiColorsPolylineOptions.colors}
                  positions={transportHistoryTrackItems.map((condition) => [
                    condition.lat,
                    condition.lon,
                    condition.can_speed ?? condition.speed,
                  ])}
                  onClick={(e) => handleHistoryPolylineClick(e, transportHistoryTrackItems[0].transport_id)}
                  optionIdxFn={(point) => {
                    const speedThresholds = [30, 80, 120];

                    for (let i = 0; i < speedThresholds.length; ++i) {
                      if (point[2] <= speedThresholds[i]) {
                        return i;
                      }
                    }

                    return speedThresholds.length;
                  }}
                />
              );
            })}

          {isHistoryOpen &&
            historyTransportPositionItems.map((historyTransport) => {
              return (
                <RotatedMarker
                  pmIgnore
                  key={historyTransport.id}
                  minTooltipZoom={config.mapMinTransportTooltipZoom}
                  icon={Leaflet.divIcon({
                    html:
                      historyTransport?.can_speed === 0
                        ? ReactDOMServer.renderToString(
                            <TransportMarkerSquareIcon fill={historyTransport.color} htmlColor={historyTransport.color} strokeWidth={2} />
                          )
                        : ReactDOMServer.renderToString(
                            <TransportMarkerIcon fill={historyTransport.color} htmlColor={historyTransport.color} strokeWidth={2} />
                          ),
                    className: 'leaflet-marker-icon-transport',
                    iconSize: [30, 30],
                    iconAnchor: [15, 15],
                  })}
                  rotationAngle={historyTransport?.can_speed !== 0 && historyTransport?.course}
                  position={[historyTransport?.lat, historyTransport?.lon]}>
                  <Tooltip direction='bottom' className='leaflet-tooltip-bottom-transport' permanent>
                    <div
                      style={{
                        padding: '4px 6px',
                        fontSize: '12px',
                        whiteSpace: 'nowrap',
                      }}>
                      {historyTransport.name}
                    </div>
                  </Tooltip>
                </RotatedMarker>
              );
            })}
        </Map>
      </Stack>
      {isHistoryOpen && <HistoryTimeLime />}
      <MapLeafletControls />
      <MapLeafletGeofenceDialog open={!!geofenceUpdatedItem} onClose={handleGeofenceDialogClose} onSuccess={handleGeofenceDialogClose} />
      <MapLeafletGeofenceShapeChangeDialog
        open={!!geofenceChangedItem}
        onClose={handleGeofenceShapeChangeDialogClose}
        onSuccess={handleGeofenceShapeChangeDialogClose}
      />
    </React.Fragment>
  );
});

export default MapLeaflet;
